import React, { useMemo, useRef } from 'react';

const toNum = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const fmt = (value, unit = '') => {
  if (value === '' || value == null) return '-';
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return `${n.toFixed(2)}${unit}`;
};

const VS_CLASS_RULES = [
  { key: 'vs-d', label: 'Vs < 180 m/s', min: -Infinity, max: 180 },
  { key: 'vs-c', label: '180 ≤ Vs < 360 m/s', min: 180, max: 360 },
  { key: 'vs-b', label: '360 ≤ Vs < 800 m/s', min: 360, max: 800 },
  { key: 'vs-a', label: 'Vs ≥ 800 m/s', min: 800, max: Infinity },
];
const CALC_DEPTH_M = 30;

const getVsClassKey = (vsValue) => {
  const n = toNum(vsValue);
  if (n == null) return 'vs-unknown';
  const found = VS_CLASS_RULES.find((rule) => n >= rule.min && n < rule.max);
  return found ? found.key : 'vs-unknown';
};

const Vs30SoilProfileSvg = ({ layers }) => {
  const uniqueIdRef = useRef(`vs30-${Math.random().toString(36).slice(2, 10)}`);
  const uniqueId = uniqueIdRef.current;
  const frameX = 76;
  const frameY = 36;
  const frameWidth = 638;
  const profileHeight = 370;
  const minBandHeight = 18;
  const legendY = frameY + profileHeight + 18;
  const svgHeight = legendY + 58;
  const axisGap = 12;
  const leftAxisX = frameX - axisGap;
  const rightAxisX = frameX + frameWidth + axisGap;

  const mappedLayers = useMemo(() => {
    const safeLayers = Array.isArray(layers) ? layers : [];
    const parsed = safeLayers.map((layer, index) => {
      const z1Num = toNum(layer?.z1);
      const z2Num = toNum(layer?.z2);
      const validDepth = z1Num != null && z2Num != null && z2Num > z1Num;
      const hiNum = validDepth ? z2Num - z1Num : toNum(layer?.th);

      return {
        id: layer?.id ?? index + 1,
        rawZ1: layer?.z1,
        rawZ2: layer?.z2,
        rawVs: layer?.Vs,
        z1Num,
        z2Num,
        hiNum,
        validDepth,
      };
    });

    const totalDepth = parsed.reduce((max, layer) => (layer.validDepth ? Math.max(max, layer.z2Num) : max), 0);
    const displayDepth = Math.max(totalDepth, CALC_DEPTH_M, parsed.length || 1);
    const validLayers = parsed.filter((layer) => layer.validDepth);
    const lastValidLayer = validLayers.length ? validLayers.reduce((prev, cur) => (cur.z2Num > prev.z2Num ? cur : prev)) : null;

    const boundaryDepths = Array.from(
      new Set([
        ...parsed.flatMap((layer) => {
          if (!layer.validDepth) return [];
          return [layer.z1Num, layer.z2Num];
        }),
        CALC_DEPTH_M,
      ]),
    ).sort((a, b) => a - b);

    return {
      parsed,
      totalDepth,
      displayDepth,
      lastValidLayer,
      hasValidDepth: parsed.some((layer) => layer.validDepth),
      boundaryDepths,
    };
  }, [layers]);

  const legendItems = useMemo(
    () => [...VS_CLASS_RULES.map((item) => ({ key: item.key, label: item.label })), { key: 'vs-unknown', label: 'Vs missing/invalid' }],
    [],
  );

  const calcDepthY = frameY + (CALC_DEPTH_M / mappedLayers.displayDepth) * profileHeight;

  const getPatternFillId = (layer) => {
    const key = getVsClassKey(layer.rawVs);
    return `${uniqueId}-${key}`;
  };

  return (
    <div className="vs30ProfileSvgContainer">
      <svg className="vs30ProfileSvg" viewBox={`0 0 800 ${svgHeight}`} width="100%" role="img" aria-label="Interactive soil profile preview">
        <defs>
          <pattern id={`${uniqueId}-vs-d`} width="8" height="8" patternUnits="userSpaceOnUse">
            <line x1="0" y1="2" x2="8" y2="2" className="vs30ProfileSvgHatchLine" />
            <line x1="0" y1="6" x2="8" y2="6" className="vs30ProfileSvgHatchLine" />
            <line x1="4" y1="0" x2="4" y2="3" className="vs30ProfileSvgHatchLine" />
          </pattern>
          <pattern id={`${uniqueId}-vs-c`} width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="7" className="vs30ProfileSvgHatchLine" />
          </pattern>
          <pattern id={`${uniqueId}-vs-b`} width="8" height="8" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="0.8" className="vs30ProfileSvgHatchDot" />
            <circle cx="6" cy="5" r="0.8" className="vs30ProfileSvgHatchDot" />
          </pattern>
          <pattern id={`${uniqueId}-vs-a`} width="12" height="12" patternUnits="userSpaceOnUse">
            <polyline points="1,10 4,6 8,7 11,3" className="vs30ProfileSvgHatchPoly" />
            <polyline points="0,2 3,1 5,4" className="vs30ProfileSvgHatchPoly" />
            <polyline points="7,11 10,9 12,11" className="vs30ProfileSvgHatchPoly" />
          </pattern>
          <pattern id={`${uniqueId}-vs-unknown`} width="8" height="8" patternUnits="userSpaceOnUse">
            <line x1="0" y1="7" x2="8" y2="7" className="vs30ProfileSvgHatchLine" />
            <line x1="7" y1="0" x2="7" y2="8" className="vs30ProfileSvgHatchLine" />
          </pattern>
        </defs>
        <style>{`
          .vs30ProfileSvgTitle { font-size: 13px; font-weight: 700; fill: #2f2f2f; }
          .vs30ProfileSvgFrame { fill: #ffffff; stroke: #9a9a9a; stroke-width: 0.8; }
          .vs30ProfileSvgGroundSurfaceLine { stroke: #4f4f4f; stroke-width: 0.8; }
          .vs30ProfileSvgGroundSurfaceTick { stroke: #4f4f4f; stroke-width: 0.7; }
          .vs30ProfileSvgGroundSurfaceIcon { fill: #4f4f4f; }
          .vs30ProfileSvgGroundSurfaceLabel { font-size: 9px; font-weight: 700; fill: #4f4f4f; }
          .vs30ProfileSvgHatchLine { stroke: #4b4b4b; stroke-width: 0.55; }
          .vs30ProfileSvgHatchDot { fill: #4b4b4b; }
          .vs30ProfileSvgHatchPoly { fill: none; stroke: #4b4b4b; stroke-width: 0.55; }
          .vs30ProfileSvgLayer { stroke: #444444; stroke-width: 0.65; opacity: 0.85; }
          .vs30ProfileSvgLayerExtrapolated { opacity: 0.85; stroke: #2f2f2f; stroke-width: 0.6; stroke-dasharray: 3 2; }
          .vs30ProfileSvgCalcDepthNote { font-size: 9px; font-weight: 700; fill: #7a4a00; text-anchor: middle; }
          .vs30ProfileSvgCalcDepthLine { stroke: #b04a4a; stroke-width: 1; stroke-dasharray: 5 3; }
          .vs30ProfileSvgCalcDepthArrow { stroke: #7a4a00; stroke-width: 0.8; }
          .vs30ProfileSvgCalcDepthArrowHead { fill: #7a4a00; }
          .vs30ProfileSvgCalcDepthLabel { font-size: 9px; font-weight: 700; fill: #9d3e3e; text-anchor: end; }
          .vs30ProfileSvgLayerIdBadge { fill: rgba(255, 255, 255, 0.9); stroke: #3f3f3f; stroke-width: 0.6; pointer-events: none; }
          .vs30ProfileSvgLayerId { font-size: 10px; font-weight: 700; fill: #1f1f1f; text-anchor: middle; pointer-events: none; }
          .vs30ProfileSvgVsBadge { fill: rgba(255, 255, 255, 0.9); stroke: #3f3f3f; stroke-width: 0.6; pointer-events: none; }
          .vs30ProfileSvgVsText { font-size: 10px; font-weight: 700; fill: #1f1f1f; text-anchor: middle; pointer-events: none; }
          .vs30ProfileSvgAxis { stroke: #6a6a6a; stroke-width: 0.7; }
          .vs30ProfileSvgAxisTick { stroke: #6a6a6a; stroke-width: 0.6; }
          .vs30ProfileSvgAxisStartLabel { font-size: 8px; font-weight: 700; fill: #5d5d5d; text-anchor: middle; }
          .vs30ProfileSvgAxisStartSymbol { fill: #6a6a6a; }
          .vs30ProfileSvgAxisLayerLabel { font-size: 9px; font-weight: 600; fill: #2f2f2f; text-anchor: end; }
          .vs30ProfileSvgAxisHiLabel { font-size: 9px; font-weight: 700; fill: #2f2f2f; text-anchor: start; }
          .vs30ProfileSvgLegendFrame { fill: #ffffff; stroke: #c2c2c2; stroke-width: 0.8; }
          .vs30ProfileSvgLegendSwatch { stroke: #555; stroke-width: 0.5; }
          .vs30ProfileSvgLegendText { font-size: 9px; fill: #3d3d3d; }
          .vs30ProfileSvgEmpty { font-size: 12px; fill: #787878; text-anchor: middle; }
        `}</style>

        <text x="400" y="22" className="vs30ProfileSvgTitle" textAnchor="middle">
          Soil Profile
        </text>

        <rect x={frameX} y={frameY} width={frameWidth} height={profileHeight} className="vs30ProfileSvgFrame" />
        <g className="vs30ProfileSvgGroundSurface">
          <line x1={frameX} y1={frameY} x2={frameX + frameWidth} y2={frameY} className="vs30ProfileSvgGroundSurfaceLine" />
          <line x1={frameX + 120} y1={frameY} x2={frameX + 124} y2={frameY - 4} className="vs30ProfileSvgGroundSurfaceTick" />
          <line x1={frameX + 320} y1={frameY} x2={frameX + 324} y2={frameY - 4} className="vs30ProfileSvgGroundSurfaceTick" />
          <line x1={frameX + 520} y1={frameY} x2={frameX + 524} y2={frameY - 4} className="vs30ProfileSvgGroundSurfaceTick" />
          <polygon
            points={`${frameX + 10},${frameY - 4} ${frameX + 7},${frameY - 10} ${frameX + 13},${frameY - 10}`}
            className="vs30ProfileSvgGroundSurfaceIcon"
          />
          <text x={frameX + 16} y={frameY - 5} className="vs30ProfileSvgGroundSurfaceLabel">
            Ground Surface
          </text>
        </g>

        {mappedLayers.parsed.length === 0 && (
          <text x="400" y={frameY + profileHeight / 2} className="vs30ProfileSvgEmpty">
            Add layers to preview the soil profile.
          </text>
        )}

        {mappedLayers.parsed.map((layer, index) => {
          let y = frameY;
          let height = profileHeight / Math.max(mappedLayers.parsed.length, 1);

          if (mappedLayers.hasValidDepth && layer.validDepth) {
            y = frameY + (layer.z1Num / mappedLayers.displayDepth) * profileHeight;
            height = ((layer.z2Num - layer.z1Num) / mappedLayers.displayDepth) * profileHeight;
          } else if (mappedLayers.hasValidDepth && !layer.validDepth) {
            height = minBandHeight;
            const yFromZ1 =
              layer.z1Num != null
                ? frameY + (Math.max(0, layer.z1Num) / mappedLayers.displayDepth) * profileHeight
                : frameY + profileHeight;
            y = Math.min(frameY + profileHeight - minBandHeight, yFromZ1);
          }

          const patternId = getPatternFillId(layer);
          const bandHeight = Math.max(height, minBandHeight);
          const vsBadgeWidth = 124;
          const vsBadgeXDefault = frameX + frameWidth / 2 - vsBadgeWidth / 2;
          const vsBadgeX = vsBadgeXDefault;
          let layerMidY = y + bandHeight / 2;
          if (layer.validDepth && mappedLayers.totalDepth > CALC_DEPTH_M && layer.z1Num < CALC_DEPTH_M) {
            const layerCalcBottom = Math.min(layer.z2Num, CALC_DEPTH_M);
            const layerCalcMidDepth = (layer.z1Num + layerCalcBottom) / 2;
            layerMidY = frameY + (layerCalcMidDepth / mappedLayers.displayDepth) * profileHeight;
          }
          const layerBadgeY = layerMidY - 7;
          const layerTextY = layerMidY + 3;
          const vsBadgeY = layerMidY - 7;
          const vsTextY = vsBadgeY + 10;

          return (
            <g key={`${layer.id}-${index}`}>
              <rect
                x={frameX}
                y={y}
                width={frameWidth}
                height={bandHeight}
                fill={`url(#${patternId})`}
                className="vs30ProfileSvgLayer"
              />
              <rect x={frameX + 8} y={layerBadgeY} width="56" height="14" rx="2" className="vs30ProfileSvgLayerIdBadge" />
              <text x={frameX + 36} y={layerTextY} className="vs30ProfileSvgLayerId">
                {`Layer-${layer.id}`}
              </text>
              <rect
                x={vsBadgeX}
                y={vsBadgeY}
                width={vsBadgeWidth}
                height="14"
                rx="2"
                className="vs30ProfileSvgVsBadge"
              />
              <text x={vsBadgeX + vsBadgeWidth / 2} y={vsTextY} className="vs30ProfileSvgVsText">
                {`Vs=${fmt(layer.rawVs, '')} m/s`}
              </text>
            </g>
          );
        })}

        {mappedLayers.hasValidDepth &&
          mappedLayers.totalDepth < CALC_DEPTH_M &&
          mappedLayers.lastValidLayer &&
          (() => {
            const extrapTopY = frameY + (mappedLayers.totalDepth / mappedLayers.displayDepth) * profileHeight;
            const extrapHeight = ((CALC_DEPTH_M - mappedLayers.totalDepth) / mappedLayers.displayDepth) * profileHeight;
            const extrapPatternId = getPatternFillId(mappedLayers.lastValidLayer);
            const noteX = frameX + frameWidth / 2;
            const noteY = extrapTopY + 12;
            return (
              <g>
                <rect
                  x={frameX}
                  y={extrapTopY}
                  width={frameWidth}
                  height={Math.max(extrapHeight, 3)}
                  fill={`url(#${extrapPatternId})`}
                  className="vs30ProfileSvgLayerExtrapolated"
                />
                <text x={noteX} y={noteY} className="vs30ProfileSvgCalcDepthNote">
                  {`Extrapolated from Layer-${mappedLayers.lastValidLayer.id} to 30 m for Vs30`}
                </text>
                <line x1={noteX} y1={noteY + 3} x2={noteX} y2={noteY + 10} className="vs30ProfileSvgCalcDepthArrow" />
                <polygon points={`${noteX - 3},${noteY + 10} ${noteX + 3},${noteY + 10} ${noteX},${noteY + 14}`} className="vs30ProfileSvgCalcDepthArrowHead" />
              </g>
            );
          })()}

        {mappedLayers.hasValidDepth && mappedLayers.totalDepth > CALC_DEPTH_M && (
          <g>
            <line
              x1={frameX}
              y1={frameY + (CALC_DEPTH_M / mappedLayers.displayDepth) * profileHeight}
              x2={frameX + frameWidth}
              y2={frameY + (CALC_DEPTH_M / mappedLayers.displayDepth) * profileHeight}
              className="vs30ProfileSvgCalcDepthLine"
            />
            <text
              x={frameX + frameWidth - 4}
              y={frameY + (CALC_DEPTH_M / mappedLayers.displayDepth) * profileHeight - 4}
              className="vs30ProfileSvgCalcDepthLabel"
            >
              30 m (Vs30 calculation limit)
            </text>
          </g>
        )}

        <line x1={leftAxisX} y1={frameY} x2={leftAxisX} y2={frameY + profileHeight} className="vs30ProfileSvgAxis" />
        <text x={leftAxisX} y={frameY - 14} className="vs30ProfileSvgAxisStartLabel">
          z
          <tspan baselineShift="sub" fontSize="7">
            i
          </tspan>
        </text>
        <polygon
          points={`${leftAxisX},${frameY - 4} ${leftAxisX - 3},${frameY - 10} ${leftAxisX + 3},${frameY - 10}`}
          className="vs30ProfileSvgAxisStartSymbol"
        />
        {mappedLayers.boundaryDepths.map((depthValue) => {
          const y = frameY + (depthValue / mappedLayers.displayDepth) * profileHeight;
          return (
            <g key={`axis-depth-${depthValue}`}>
              <line x1={leftAxisX - 4} y1={y} x2={leftAxisX} y2={y} className="vs30ProfileSvgAxisTick" />
              <text x={leftAxisX - 6} y={y + 3} className="vs30ProfileSvgAxisLayerLabel">
                {`${fmt(depthValue, '')} m`}
              </text>
            </g>
          );
        })}

        <line
          x1={rightAxisX}
          y1={frameY}
          x2={rightAxisX}
          y2={frameY + profileHeight}
          className="vs30ProfileSvgAxis"
        />
        <text x={rightAxisX} y={frameY - 14} className="vs30ProfileSvgAxisStartLabel">
          H
          <tspan baselineShift="sub" fontSize="7">
            i
          </tspan>
        </text>
        <polygon points={`${rightAxisX},${frameY - 4} ${rightAxisX - 3},${frameY - 10} ${rightAxisX + 3},${frameY - 10}`} className="vs30ProfileSvgAxisStartSymbol" />
        <line x1={rightAxisX} y1={frameY + profileHeight} x2={rightAxisX + 4} y2={frameY + profileHeight} className="vs30ProfileSvgAxisTick" />
        {mappedLayers.boundaryDepths.map((depthValue) => {
          if (mappedLayers.totalDepth > CALC_DEPTH_M && depthValue > CALC_DEPTH_M) return null;
          if (mappedLayers.totalDepth < CALC_DEPTH_M) {
            const EPS = 1e-9;
            if (Math.abs(depthValue - mappedLayers.totalDepth) < EPS && mappedLayers.totalDepth > 0) return null;
          }
          const y = frameY + (depthValue / mappedLayers.displayDepth) * profileHeight;
          return <line key={`axis-depth-right-${depthValue}`} x1={rightAxisX} y1={y} x2={rightAxisX + 4} y2={y} className="vs30ProfileSvgAxisTick" />;
        })}
        {mappedLayers.parsed.map((layer, index) => {
          if (!layer.validDepth) return null;
          if (mappedLayers.totalDepth > CALC_DEPTH_M && layer.z1Num >= CALC_DEPTH_M) return null;
          const calcTop = layer.z1Num;
          const isLastValidLayer = mappedLayers.lastValidLayer === layer;
          let calcBottom = layer.z2Num;
          if (mappedLayers.totalDepth > CALC_DEPTH_M) {
            calcBottom = Math.min(layer.z2Num, CALC_DEPTH_M);
          } else if (mappedLayers.totalDepth < CALC_DEPTH_M && isLastValidLayer) {
            calcBottom = CALC_DEPTH_M;
          }
          const hiValue = calcBottom - calcTop;
          if (!(hiValue > 0)) return null;
          const midDepth = (calcTop + calcBottom) / 2;
          const y = frameY + (midDepth / mappedLayers.displayDepth) * profileHeight;

          return (
            <g key={`axis-hi-${layer.id}-${index}`}>
              <text x={rightAxisX + 8} y={y + 3} className="vs30ProfileSvgAxisHiLabel">
                H
                <tspan baselineShift="sub" fontSize="7">
                  {layer.id}
                </tspan>
                {`=${fmt(hiValue, '')} m`}
              </text>
            </g>
          );
        })}

        <g>
          <rect x={frameX} y={legendY} width={frameWidth} height="44" rx="4" className="vs30ProfileSvgLegendFrame" />
          {legendItems.map((item, i) => {
            const col = i % 3;
            const row = Math.floor(i / 3);
            const colWidth = (frameWidth - 24) / 3;
            const x = frameX + 12 + col * colWidth;
            const y = legendY + 14 + row * 16;
            return (
              <g key={`legend-${item.key}`}>
                <rect x={x} y={y - 9} width="14" height="10" fill={`url(#${uniqueId}-${item.key})`} className="vs30ProfileSvgLegendSwatch" />
                <text x={x + 20} y={y} className="vs30ProfileSvgLegendText">
                  {item.label}
                </text>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
};

export default Vs30SoilProfileSvg;
